import { apiCall, isBackendConfigured } from './api-call'
import {
  MaintenanceCategory,
  MaintenanceRequestPriority,
  AmenitySlot,
  RenterBulletin,
  RenterPoll,
  ResidentAmenity,
  ResidentAmenityBooking,
  ResidentBulletinAcknowledgement,
  ResidentDocument,
  ResidentLedger,
  ResidentMaintenanceRequest,
  ResidentPaymentMethodKind,
  ResidentPost,
  ResidentPostReply,
  ResidentPostReportReason,
  ResidentPostType,
  ResidentTenancy,
  RenterPost,
  RenterPostView,
  RenterReplyView,
  FeedAvailability,
  ResidentLeaseOffer,
  ResidentSecurityDeposit,
  ResidentMessage,
  RenterPaymentOptions,
  ResidentPayerLedger,
  TenancyPayerLedger,
  ChargeSettlementReport,
} from '../_types/resident'

const RESIDENT_GROUP = 'residentportal'

/**
 * Claims an invited tenancy for the signed-in renter using the invite token.
 * Resolves with the activated tenancy. Throws an Error carrying the backend
 * message on failure (expired / already-used / wrong-email) so the claim page
 * can surface the specific reason.
 *
 * @param {string} token - the invite token from the claim link
 * @returns {Promise<ResidentTenancy>}
 */
const claimTenancy = async (token: string): Promise<ResidentTenancy> => {
  if (isBackendConfigured() === false) {
    throw new Error('QuickPass is not connected to the backend right now.')
  }

  const response = await apiCall({
    group: RESIDENT_GROUP,
    command: 'claimtenancy',
    data: { token },
  })

  if (response.code !== 200) {
    throw new Error(response.message)
  }

  return JSON.parse(response.data) as ResidentTenancy
}

/**
 * Lists the signed-in renter's own tenancies. The renter is identified by their
 * Firebase token, so no id is sent. Returns an empty array on failure.
 *
 * @returns {Promise<ResidentTenancy[]>}
 */
const getMyTenancies = async (): Promise<ResidentTenancy[]> => {
  if (isBackendConfigured() === false) {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getmytenancies',
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentTenancy[]
  } catch (error) {
    console.warn('[resident] getMyTenancies failed —', error)
    return []
  }
}

/**
 * Fetches the signed-in renter's ledger for one of their tenancies. Returns
 * null on failure.
 *
 * @param {string} tenancyId - the tenancy whose ledger to fetch
 * @returns {Promise<ResidentLedger | null>}
 */
const getMyLedger = async (
  tenancyId: string,
): Promise<ResidentLedger | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getledger',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentLedger
  } catch (error) {
    console.warn('[resident] getMyLedger failed —', error)
    return null
  }
}

/**
 * Fetches the payment options for a tenancy's Payments surface — whether card
 * is available for this property manager and whether it carries an added fee.
 * Returns null on failure (the caller falls back to the launch defaults; the
 * server enforces the real policy either way).
 *
 * @param {string} tenancyId - the tenancy whose payment options to fetch
 * @returns {Promise<RenterPaymentOptions | null>}
 */
const getPaymentOptions = async (
  tenancyId: string,
): Promise<RenterPaymentOptions | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getpaymentoptions',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as RenterPaymentOptions
  } catch (error) {
    console.warn('[resident] getPaymentOptions failed —', error)
    return null
  }
}

/**
 * Fetches this renter's own share of a split rent, or null when the home's rent
 * is not split (or the fetch fails).
 *
 * The server returns only the signed-in renter's row, so there is nothing here
 * about what a roommate owes or whether they have missed a payment.
 *
 * A null return is treated as "not split" by the caller, which is the safe
 * direction: the surface falls back to showing the home's balance, and the server
 * decides what any payment is actually for regardless of what was displayed.
 *
 * @param {string} tenancyId - the tenancy whose split to read
 * @returns {Promise<ResidentPayerLedger | null>}
 */
const getMyRentShare = async (
  tenancyId: string,
): Promise<ResidentPayerLedger | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getrentshares',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const split = JSON.parse(response.data) as TenancyPayerLedger
    return split.payers.length === 0 ? null : split.payers[0]
  } catch (error) {
    console.warn('[resident] getMyRentShare failed —', error)
    return null
  }
}

interface RentCheckoutResult {
  checkoutUrl: string
  sessionId: string
}

/**
 * Starts a rent payment for a tenancy's outstanding balance by the chosen
 * method (bank = no added fee; card = a processing fee is added). Returns the
 * Stripe-hosted Checkout URL to redirect to, or null on failure. The amount is
 * decided server-side from the ledger balance, never sent from here.
 *
 * @param {string} tenancyId - the tenancy to pay
 * @param {ResidentPaymentMethodKind} paymentMethodKind - 'bank' or 'card'
 * @returns {Promise<RentCheckoutResult | null>}
 */
const createRentCheckout = async (
  tenancyId: string,
  paymentMethodKind: ResidentPaymentMethodKind,
): Promise<RentCheckoutResult | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  const baseUrl = window.location.origin

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'createrentcheckout',
      data: {
        tenancyId,
        paymentMethodKind,
        successUrl: `${baseUrl}/#/payments?payment=success`,
        cancelUrl: `${baseUrl}/#/payments?payment=cancel`,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const result = JSON.parse(response.data) as RentCheckoutResult
    if (result.checkoutUrl === '') {
      return null
    }

    return result
  } catch (error) {
    console.warn('[resident] createRentCheckout failed —', error)
    return null
  }
}

interface RentPaymentIntentResult {
  clientSecret: string
  paymentIntentId: string
  rentCents: number
  feeCents: number
  totalCents: number
}

/**
 * The breakdown for paying ONE charge. `chargeDescription` is echoed back from
 * the server so the pay page names what is actually being paid for instead of
 * saying rent.
 */
interface ChargePaymentIntentResult {
  clientSecret: string
  paymentIntentId: string
  chargeId: string
  chargeDescription: string
  chargeCents: number
  feeCents: number
  totalCents: number
}

/**
 * Starts an embedded rent payment — a PaymentIntent whose client secret the
 * in-app Payment Element confirms against, plus the cents breakdown the pay
 * page renders as its fee disclosure. Returns null on failure so the caller
 * can fall back to the hosted Checkout flow. The amount is decided server-side
 * from the ledger balance, never sent from here. A fresh intent is created per
 * call — the pay page must never reuse one across visits.
 *
 * @param {string} tenancyId - the tenancy to pay
 * @param {ResidentPaymentMethodKind} paymentMethodKind - 'bank' or 'card'
 * @returns {Promise<RentPaymentIntentResult | null>}
 */
const createRentPaymentIntent = async (
  tenancyId: string,
  paymentMethodKind: ResidentPaymentMethodKind,
): Promise<RentPaymentIntentResult | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'createrentpaymentintent',
      data: {
        tenancyId,
        paymentMethodKind,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const result = JSON.parse(response.data) as RentPaymentIntentResult
    if (result.clientSecret === '') {
      return null
    }

    return result
  } catch (error) {
    console.warn('[resident] createRentPaymentIntent failed —', error)
    return null
  }
}

/**
 * Fetches the itemised view of a tenancy's ledger: what each individual charge
 * still owes. Returns null on failure, which the payments page treats as "show
 * the balance on its own" rather than as an error worth interrupting anybody
 * over — paying everything always works regardless.
 *
 * @param {string} tenancyId - the tenancy whose charges to fetch
 * @returns {Promise<ChargeSettlementReport | null>}
 */
const getChargeBreakdown = async (
  tenancyId: string,
): Promise<ChargeSettlementReport | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getchargebreakdown',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ChargeSettlementReport
  } catch (error) {
    console.warn('[resident] getChargeBreakdown failed —', error)
    return null
  }
}

/**
 * Starts an embedded payment for ONE charge rather than for the whole balance,
 * so a resident can pay a replacement key without paying their rent early.
 *
 * The amount is decided server-side from that charge's outstanding figure and
 * is never sent from here. Unlike the rent path there is deliberately NO hosted
 * Checkout fallback: no hosted flow can price a single charge, and falling back
 * to one would silently charge the entire balance instead of the one line the
 * resident tapped. A failure here has to stay a failure.
 *
 * @param {string} tenancyId - the tenancy the charge belongs to
 * @param {string} chargeId - the charge being paid
 * @param {ResidentPaymentMethodKind} paymentMethodKind - 'bank' or 'card'
 * @returns {Promise<ChargePaymentIntentResult | null>}
 */
const createChargePaymentIntent = async (
  tenancyId: string,
  chargeId: string,
  paymentMethodKind: ResidentPaymentMethodKind,
): Promise<ChargePaymentIntentResult | null> => {
  if (isBackendConfigured() === false || tenancyId === '' || chargeId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'createchargepaymentintent',
      data: {
        tenancyId,
        chargeId,
        paymentMethodKind,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const result = JSON.parse(response.data) as ChargePaymentIntentResult
    if (result.clientSecret === '') {
      return null
    }

    return result
  } catch (error) {
    console.warn('[resident] createChargePaymentIntent failed —', error)
    return null
  }
}

/**
 * Starts autopay enrollment — a Stripe setup-mode Checkout that saves a
 * recurring PAD mandate. Returns the hosted Checkout URL to redirect to, or
 * null on failure. No money moves here; later monthly charges are drawn against
 * the saved mandate.
 *
 * @param {string} tenancyId - the tenancy to enrol in autopay
 * @returns {Promise<RentCheckoutResult | null>}
 */
const createAutopaySetup = async (
  tenancyId: string,
): Promise<RentCheckoutResult | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  const baseUrl = window.location.origin

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'createautopaysetup',
      data: {
        tenancyId,
        successUrl: `${baseUrl}/#/payments?autopay=success`,
        cancelUrl: `${baseUrl}/#/payments?autopay=cancel`,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const result = JSON.parse(response.data) as RentCheckoutResult
    if (result.checkoutUrl === '') {
      return null
    }

    return result
  } catch (error) {
    console.warn('[resident] createAutopaySetup failed —', error)
    return null
  }
}

interface AutopaySetupIntentResult {
  clientSecret: string
  setupIntentId: string
}

/**
 * Starts embedded autopay enrollment — a SetupIntent whose client secret the
 * in-app Payment Element confirms against. No money moves; the saved mandate
 * is captured server-side by the Stripe webhook. Returns null on failure so
 * the caller can fall back to the hosted Checkout flow.
 *
 * @param {string} tenancyId - the tenancy to enrol in autopay
 * @returns {Promise<AutopaySetupIntentResult | null>}
 */
const createAutopaySetupIntent = async (
  tenancyId: string,
): Promise<AutopaySetupIntentResult | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'createautopaysetupintent',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const result = JSON.parse(response.data) as AutopaySetupIntentResult
    if (result.clientSecret === '') {
      return null
    }

    return result
  } catch (error) {
    console.warn('[resident] createAutopaySetupIntent failed —', error)
    return null
  }
}

/**
 * Turns autopay on or off for a tenancy. Used by the renter to cancel autopay
 * (the saved mandate is kept so they can re-enable without re-entering details).
 *
 * @param {string} tenancyId - the tenancy whose autopay to change
 * @param {boolean} isEnabled - the new autopay state
 * @returns {Promise<boolean>} true when the update succeeded
 */
const setAutopay = async (
  tenancyId: string,
  isEnabled: boolean,
): Promise<boolean> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return false
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'setautopay',
      data: { tenancyId, isEnabled },
    })

    return response.code === 200
  } catch (error) {
    console.warn('[resident] setAutopay failed —', error)
    return false
  }
}

interface SubmitMaintenanceFields {
  title: string
  description: string
  categoryId: string | null
  priority: MaintenanceRequestPriority
  imageUrls: string[]
}

/**
 * Lists the renter-selectable maintenance categories for a tenancy's company
 * (active only, ordered). Returns an empty array on failure or when the backend
 * is not configured — the report form then simply omits the category picker.
 *
 * @param {string} tenancyId - the tenancy whose company categories to list
 * @returns {Promise<MaintenanceCategory[]>}
 */
const getMaintenanceCategories = async (
  tenancyId: string,
): Promise<MaintenanceCategory[]> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getmaintenancecategories',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as MaintenanceCategory[]
  } catch (error) {
    console.warn('[resident] getMaintenanceCategories failed —', error)
    return []
  }
}

/**
 * Lists the renter's maintenance requests for a tenancy (newest first). Returns
 * an empty array on failure.
 *
 * @param {string} tenancyId - the tenancy whose requests to list
 * @returns {Promise<ResidentMaintenanceRequest[]>}
 */
const getMaintenance = async (
  tenancyId: string,
): Promise<ResidentMaintenanceRequest[]> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getmaintenance',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentMaintenanceRequest[]
  } catch (error) {
    console.warn('[resident] getMaintenance failed —', error)
    return []
  }
}

/**
 * Submits a new maintenance request for a tenancy. Returns the created request,
 * or null on failure.
 *
 * @param {string} tenancyId - the tenancy to file against
 * @param {SubmitMaintenanceFields} fields - title, description, category, priority
 * @returns {Promise<ResidentMaintenanceRequest | null>}
 */
const submitMaintenance = async (
  tenancyId: string,
  fields: SubmitMaintenanceFields,
): Promise<ResidentMaintenanceRequest | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'submitmaintenance',
      data: { tenancyId, ...fields },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentMaintenanceRequest
  } catch (error) {
    console.warn('[resident] submitMaintenance failed —', error)
    return null
  }
}

/**
 * Lists the announcements the renter should see for one of their tenancies (the
 * PM's published broadcasts for that residence, newest first). Returns an empty
 * array on failure.
 *
 * @param {string} tenancyId - the tenancy whose announcement feed to fetch
 * @returns {Promise<RenterBulletin[]>}
 */
const getMyAnnouncements = async (
  tenancyId: string,
): Promise<RenterBulletin[]> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'listbulletins',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as RenterBulletin[]
  } catch (error) {
    console.warn('[resident] getMyAnnouncements failed —', error)
    return []
  }
}

/**
 * Lists the documents the property manager has attached to one of the renter's
 * tenancies (lease, notices, receipts), newest first. Read-only — the renter
 * never uploads. Returns an empty array on failure.
 *
 * @param {string} tenancyId - the tenancy whose documents to fetch
 * @returns {Promise<ResidentDocument[]>}
 */
const getMyDocuments = async (
  tenancyId: string,
): Promise<ResidentDocument[]> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'listtenancydocuments',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentDocument[]
  } catch (error) {
    console.warn('[resident] getMyDocuments failed —', error)
    return []
  }
}

/**
 * Records the renter's acknowledgement of an announcement that requires one
 * ("I've read this"). Idempotent server-side. Resolves with the acknowledgement
 * timestamp (the original one if already acknowledged), or null on failure.
 *
 * @param {string} tenancyId - the tenancy the announcement is being read under
 * @param {string} bulletinId - the announcement to acknowledge
 * @returns {Promise<number | null>} the acknowledged-at timestamp, or null
 */
const acknowledgeAnnouncement = async (
  tenancyId: string,
  bulletinId: string,
): Promise<number | null> => {
  if (
    isBackendConfigured() === false ||
    tenancyId === '' ||
    bulletinId === ''
  ) {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'acknowledgebulletin',
      data: { tenancyId, bulletinId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const acknowledgement = JSON.parse(
      response.data,
    ) as ResidentBulletinAcknowledgement
    return acknowledgement.acknowledgedAt
  } catch (error) {
    console.warn('[resident] acknowledgeAnnouncement failed —', error)
    return null
  }
}

/**
 * Lists the polls the renter should see for one of their tenancies, each with
 * its aggregated results and the renter's own choice (myOptionId). Returns an
 * empty array on failure.
 *
 * @param {string} tenancyId - the tenancy whose poll feed to fetch
 * @returns {Promise<RenterPoll[]>}
 */
const getMyPolls = async (tenancyId: string): Promise<RenterPoll[]> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'listpolls',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as RenterPoll[]
  } catch (error) {
    console.warn('[resident] getMyPolls failed —', error)
    return []
  }
}

/**
 * Casts the renter's vote on a poll. One vote per renter — idempotent
 * server-side (a second tap keeps the original). Resolves true on success.
 *
 * @param {string} tenancyId - the tenancy the poll is being voted under
 * @param {string} pollId - the poll to vote on
 * @param {string} optionId - the chosen option
 * @returns {Promise<boolean>} true when the vote was recorded
 */
const votePoll = async (
  tenancyId: string,
  pollId: string,
  optionId: string,
): Promise<boolean> => {
  if (
    isBackendConfigured() === false ||
    tenancyId === '' ||
    pollId === '' ||
    optionId === ''
  ) {
    return false
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'votepoll',
      data: { tenancyId, pollId, optionId },
    })

    return response.code === 200
  } catch (error) {
    console.warn('[resident] votePoll failed —', error)
    return false
  }
}

/**
 * Lists the active amenities a renter can browse for one of their tenancies
 * (the company's amenities for that building). Read-only in this slice — booking
 * lands in a later slice. Returns an empty array on failure.
 *
 * @param {string} tenancyId - the tenancy whose building's amenities to fetch
 * @returns {Promise<ResidentAmenity[]>}
 */
const getBuildingAmenities = async (
  tenancyId: string,
): Promise<ResidentAmenity[]> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'listamenities',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentAmenity[]
  } catch (error) {
    console.warn('[resident] getBuildingAmenities failed —', error)
    return []
  }
}

/**
 * Lists the bookable slots for an amenity over a date range (ISO "YYYY-MM-DD"
 * days, end exclusive). Returns an empty array on failure.
 *
 * @param {string} tenancyId - the renter's tenancy (for the building scope)
 * @param {string} amenityId - the amenity to check
 * @param {string} startDate - inclusive range start "YYYY-MM-DD"
 * @param {string} endDate - exclusive range end "YYYY-MM-DD"
 * @returns {Promise<AmenitySlot[]>}
 */
const getAmenityAvailability = async (
  tenancyId: string,
  amenityId: string,
  startDate: string,
  endDate: string,
): Promise<AmenitySlot[]> => {
  if (isBackendConfigured() === false || tenancyId === '' || amenityId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getamenityavailability',
      data: { tenancyId, amenityId, startDate, endDate },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as AmenitySlot[]
  } catch (error) {
    console.warn('[resident] getAmenityAvailability failed —', error)
    return []
  }
}

/**
 * Books an amenity slot. The server re-validates the slot + holds it
 * transactionally; a 409 (slot just taken) surfaces as null. Resolves with the
 * created booking, or null on failure.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {string} amenityId - the amenity to book
 * @param {number} startTime - the chosen slot start (epoch ms)
 * @returns {Promise<ResidentAmenityBooking | null>}
 */
const bookAmenity = async (
  tenancyId: string,
  amenityId: string,
  startTime: number,
): Promise<ResidentAmenityBooking | null> => {
  if (isBackendConfigured() === false || tenancyId === '' || amenityId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'bookamenity',
      data: { tenancyId, amenityId, startTime },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentAmenityBooking
  } catch (error) {
    console.warn('[resident] bookAmenity failed —', error)
    return null
  }
}

/**
 * Lists the renter's amenity bookings for a tenancy (newest slot first). Returns
 * an empty array on failure.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @returns {Promise<ResidentAmenityBooking[]>}
 */
const getMyAmenityBookings = async (
  tenancyId: string,
): Promise<ResidentAmenityBooking[]> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'listamenitybookings',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentAmenityBooking[]
  } catch (error) {
    console.warn('[resident] getMyAmenityBookings failed —', error)
    return []
  }
}

/**
 * Starts a paid amenity booking: creates a pending-payment booking + a Stripe
 * Checkout session for the refundable deposit hold, and resolves with the
 * checkout URL to redirect to (or null on failure). The booking confirms once
 * the hold is placed (via the webhook).
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {string} amenityId - the paid amenity to book
 * @param {number} startTime - the chosen slot start (epoch ms)
 * @returns {Promise<string | null>} the Stripe Checkout URL, or null
 */
const startAmenityDeposit = async (
  tenancyId: string,
  amenityId: string,
  startTime: number,
): Promise<string | null> => {
  if (isBackendConfigured() === false || tenancyId === '' || amenityId === '') {
    return null
  }

  const baseUrl = window.location.origin

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'startamenitydeposit',
      data: {
        tenancyId,
        amenityId,
        startTime,
        successUrl: `${baseUrl}/#/amenities?deposit=success`,
        cancelUrl: `${baseUrl}/#/amenities/${amenityId}`,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const result = JSON.parse(response.data) as { checkoutUrl: string }
    if (result.checkoutUrl === '') {
      return null
    }

    return result.checkoutUrl
  } catch (error) {
    console.warn('[resident] startAmenityDeposit failed —', error)
    return null
  }
}

/**
 * Cancels one of the renter's amenity bookings, releasing its slot. Resolves
 * true when the cancellation succeeded.
 *
 * @param {string} bookingId - the booking to cancel
 * @returns {Promise<boolean>}
 */
const cancelMyAmenityBooking = async (bookingId: string): Promise<boolean> => {
  if (isBackendConfigured() === false || bookingId === '') {
    return false
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'cancelamenitybooking',
      data: { bookingId },
    })

    return response.code === 200
  } catch (error) {
    console.warn('[resident] cancelMyAmenityBooking failed —', error)
    return false
  }
}

/**
 * Lists the visible community posts of one mode for the renter's building, each
 * tagged with whether the renter authored it. Returns [] on failure.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {ResidentPostType} postType - 'marketplace' or 'lost-found'
 * @returns {Promise<RenterPost[]>}
 */
const getCommunityPosts = async (
  tenancyId: string,
  postType: ResidentPostType,
): Promise<RenterPost[]> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'listposts',
      data: { tenancyId, postType },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as RenterPost[]
  } catch (error) {
    console.warn('[resident] getCommunityPosts failed —', error)
    return []
  }
}

/**
 * Creates a community post for the renter's building. Resolves with the created
 * post, or null on failure.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {ResidentPostType} postType - 'marketplace' or 'lost-found'
 * @param {string} category - the post category
 * @param {string} title - the post title
 * @param {string} body - the post body
 * @param {string[]} imageUrls - already-uploaded post image URLs (marketplace / lost-found)
 * @returns {Promise<ResidentPost | null>}
 */
const createCommunityPost = async (
  tenancyId: string,
  postType: ResidentPostType,
  category: string,
  title: string,
  body: string,
  imageUrls: string[],
): Promise<ResidentPost | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'createpost',
      data: { tenancyId, postType, category, title, body, imageUrls },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentPost
  } catch (error) {
    console.warn('[resident] createCommunityPost failed —', error)
    return null
  }
}

/**
 * Uploads a single community-post image (a base64 data URL, compressed
 * client-side first) and resolves with its public URL, or null on failure. The
 * returned URL is what createCommunityPost stores in imageUrls.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {string} imageDataUrl - a `data:image/...;base64,...` data URL
 * @returns {Promise<string | null>}
 */
const uploadCommunityPostImage = async (
  tenancyId: string,
  imageDataUrl: string,
): Promise<string | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'uploadpostimage',
      data: { tenancyId, imageDataUrl },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return (JSON.parse(response.data) as { url: string }).url
  } catch (error) {
    console.warn('[resident] uploadCommunityPostImage failed —', error)
    return null
  }
}

/**
 * Uploads a single maintenance photo (a base64 data URL, compressed client-side
 * first) and resolves with its public URL, or null on failure. The returned URL
 * is what submitMaintenance stores in imageUrls.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {string} imageDataUrl - a `data:image/...;base64,...` data URL
 * @returns {Promise<string | null>}
 */
const uploadMaintenanceImage = async (
  tenancyId: string,
  imageDataUrl: string,
): Promise<string | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'uploadmaintenanceimage',
      data: { tenancyId, imageDataUrl },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return (JSON.parse(response.data) as { url: string }).url
  } catch (error) {
    console.warn('[resident] uploadMaintenanceImage failed —', error)
    return null
  }
}

/**
 * Lists the visible replies on a community post. Returns [] on failure.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {string} postId - the post whose replies to load
 * @returns {Promise<ResidentPostReply[]>}
 */
const getCommunityPostReplies = async (
  tenancyId: string,
  postId: string,
): Promise<RenterReplyView[]> => {
  if (isBackendConfigured() === false || tenancyId === '' || postId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'listpostreplies',
      data: { tenancyId, postId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as RenterReplyView[]
  } catch (error) {
    console.warn('[resident] getCommunityPostReplies failed —', error)
    return []
  }
}

/**
 * Adds a reply to a community post. Resolves with the created reply, or null.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {string} postId - the post to reply to
 * @param {string} body - the reply text
 * @returns {Promise<ResidentPostReply | null>}
 */
const replyToCommunityPost = async (
  tenancyId: string,
  postId: string,
  body: string,
): Promise<ResidentPostReply | null> => {
  if (isBackendConfigured() === false || tenancyId === '' || postId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'createreply',
      data: { tenancyId, postId, body },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentPostReply
  } catch (error) {
    console.warn('[resident] replyToCommunityPost failed —', error)
    return null
  }
}

/**
 * Reports a community post (or a reply, when replyId is set) to the PM. Resolves
 * true when the report was filed.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {string} postId - the reported post
 * @param {string | null} replyId - the reported reply, or null for the post
 * @param {ResidentPostReportReason} reason - the report reason
 * @param {string} detail - optional free-text detail
 * @returns {Promise<boolean>}
 */
const reportCommunityPost = async (
  tenancyId: string,
  postId: string,
  replyId: string | null,
  reason: ResidentPostReportReason,
  detail: string,
): Promise<boolean> => {
  if (isBackendConfigured() === false || tenancyId === '' || postId === '') {
    return false
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'reportpost',
      data: { tenancyId, postId, replyId, reason, detail },
    })

    return response.code === 200
  } catch (error) {
    console.warn('[resident] reportCommunityPost failed —', error)
    return false
  }
}

/**
 * Sets the resolution of the renter's OWN post (open / resolved). Resolves with
 * the updated post, or null.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {string} postId - the post to update
 * @param {'open' | 'resolved'} resolution - the new resolution
 * @returns {Promise<ResidentPost | null>}
 */
const setCommunityPostResolution = async (
  tenancyId: string,
  postId: string,
  resolution: 'open' | 'resolved',
): Promise<ResidentPost | null> => {
  if (isBackendConfigured() === false || tenancyId === '' || postId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'resolvepost',
      data: { tenancyId, postId, resolution },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentPost
  } catch (error) {
    console.warn('[resident] setCommunityPostResolution failed —', error)
    return null
  }
}

/**
 * Removes the renter's OWN post. Resolves true when removed.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {string} postId - the post to remove
 * @returns {Promise<boolean>}
 */
const removeCommunityPost = async (
  tenancyId: string,
  postId: string,
): Promise<boolean> => {
  if (isBackendConfigured() === false || tenancyId === '' || postId === '') {
    return false
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'removepost',
      data: { tenancyId, postId },
    })

    return response.code === 200
  } catch (error) {
    console.warn('[resident] removeCommunityPost failed —', error)
    return false
  }
}

/**
 * Resolves whether the community Feed is available for the renter's building.
 * Returns null on failure (treated as unavailable).
 *
 * @param {string} tenancyId - the renter's tenancy
 * @returns {Promise<FeedAvailability | null>}
 */
const getFeedAvailability = async (
  tenancyId: string,
): Promise<FeedAvailability | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getfeedavailability',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as FeedAvailability
  } catch (error) {
    console.warn('[resident] getFeedAvailability failed —', error)
    return null
  }
}

/**
 * Whether the renter has accepted their building's Feed terms. Returns false on
 * failure (gate stays closed).
 *
 * @param {string} tenancyId - the renter's tenancy
 * @returns {Promise<boolean>}
 */
const getFeedTermsAccepted = async (tenancyId: string): Promise<boolean> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return false
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getfeedterms',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const acceptance = JSON.parse(response.data) as { accepted: boolean }
    return acceptance.accepted === true
  } catch (error) {
    console.warn('[resident] getFeedTermsAccepted failed —', error)
    return false
  }
}

/**
 * Records the renter's acceptance of their building's Feed terms. Resolves true
 * on success.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @returns {Promise<boolean>}
 */
const acceptFeedTerms = async (tenancyId: string): Promise<boolean> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return false
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'acceptfeedterms',
      data: { tenancyId },
    })

    return response.code === 200
  } catch (error) {
    console.warn('[resident] acceptFeedTerms failed —', error)
    return false
  }
}

/**
 * Casts an up/down vote on a feed post (re-tapping the same direction retracts
 * it). Resolves with the updated post view (new counts), or null on failure.
 *
 * @param {string} tenancyId - the renter's tenancy
 * @param {string} postId - the feed post
 * @param {1 | -1} value - 1 = upvote, -1 = downvote
 * @returns {Promise<RenterPostView | null>}
 */
const voteFeedPost = async (
  tenancyId: string,
  postId: string,
  value: 1 | -1,
): Promise<RenterPostView | null> => {
  if (isBackendConfigured() === false || tenancyId === '' || postId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'votepost',
      data: { tenancyId, postId, value },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as RenterPostView
  } catch (error) {
    console.warn('[resident] voteFeedPost failed —', error)
    return null
  }
}

/**
 * Lists the resident's lease renewal offers for a tenancy (newest first). Returns
 * an empty array on failure.
 *
 * @param {string} tenancyId - the resident's tenancy.
 * @returns {Promise<ResidentLeaseOffer[]>}
 */
const getMyLeaseOffers = async (
  tenancyId: string,
): Promise<ResidentLeaseOffer[]> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'listleaseoffers',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentLeaseOffer[]
  } catch (error) {
    console.warn('[resident] getMyLeaseOffers failed —', error)
    return []
  }
}

/**
 * Responds to a lease offer. Accepting is an e-signature — it sends the renter's
 * typed legal name and, server-side, advances the tenancy to the offered terms.
 * Declining records the decline. Returns the updated offer, or null on failure.
 *
 * @param {string} offerId - the offer being responded to.
 * @param {'accept' | 'decline'} decision - accept (sign) or decline.
 * @param {string} signerName - the renter's typed legal name (required to accept).
 * @returns {Promise<ResidentLeaseOffer | null>}
 */
const respondToLeaseOffer = async (
  offerId: string,
  decision: 'accept' | 'decline',
  signerName: string,
): Promise<ResidentLeaseOffer | null> => {
  if (isBackendConfigured() === false || offerId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'respondtoleaseoffer',
      data: { offerId, decision, signerName },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentLeaseOffer
  } catch (error) {
    console.warn('[resident] respondToLeaseOffer failed —', error)
    return null
  }
}

/**
 * Fetches the renter's security deposit for a tenancy (the PM's internal notes
 * are blanked server-side). Returns null when there's no deposit or on failure.
 *
 * @param {string} tenancyId - the renter's tenancy.
 * @returns {Promise<ResidentSecurityDeposit | null>}
 */
const getMyDeposit = async (
  tenancyId: string,
): Promise<ResidentSecurityDeposit | null> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getmydeposit',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentSecurityDeposit
  } catch (error) {
    console.warn('[resident] getMyDeposit failed —', error)
    return null
  }
}

/**
 * Lists the resident's PM↔resident message thread for a tenancy (oldest first).
 * Returns an empty array on failure.
 *
 * @param {string} tenancyId - the resident's tenancy.
 * @returns {Promise<ResidentMessage[]>}
 */
const getMyMessages = async (
  tenancyId: string,
): Promise<ResidentMessage[]> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return []
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'listresidentmessages',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentMessage[]
  } catch (error) {
    console.warn('[resident] getMyMessages failed —', error)
    return []
  }
}

/**
 * Sends a reply from the resident in their PM↔resident thread. Returns the
 * created message, or null on failure.
 *
 * @param {string} tenancyId - the resident's tenancy.
 * @param {string} body - the message text.
 * @param {string} senderName - the resident's display name.
 * @returns {Promise<ResidentMessage | null>}
 */
const sendMyMessage = async (
  tenancyId: string,
  body: string,
  senderName: string,
): Promise<ResidentMessage | null> => {
  if (isBackendConfigured() === false || tenancyId === '' || body === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'sendmymessage',
      data: { tenancyId, body, senderName },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentMessage
  } catch (error) {
    console.warn('[resident] sendMyMessage failed —', error)
    return null
  }
}

/**
 * Marks the property manager's messages in this thread as read (called when the
 * resident opens their messages). Best-effort — returns true on success.
 *
 * @param {string} tenancyId - the resident's tenancy.
 * @returns {Promise<boolean>}
 */
const markMyThreadRead = async (tenancyId: string): Promise<boolean> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return false
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'markthreadread',
      data: { tenancyId },
    })
    return response.code === 200
  } catch (error) {
    console.warn('[resident] markMyThreadRead failed —', error)
    return false
  }
}

/**
 * Counts the resident's unread messages for a tenancy (PM-sent, not yet read) —
 * for the nav badge. Returns 0 on failure.
 *
 * @param {string} tenancyId - the resident's tenancy.
 * @returns {Promise<number>}
 */
const getMyMessagesUnreadCount = async (
  tenancyId: string,
): Promise<number> => {
  if (isBackendConfigured() === false || tenancyId === '') {
    return 0
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'getmyunreadcount',
      data: { tenancyId },
    })

    if (response.code !== 200) {
      return 0
    }

    const parsed = JSON.parse(response.data) as { unreadCount: number }
    return parsed.unreadCount
  } catch (error) {
    console.warn('[resident] getMyMessagesUnreadCount failed —', error)
    return 0
  }
}

/**
 * Records the resident's acknowledgement of a warning. Returns the updated
 * message, or null on failure.
 *
 * @param {string} tenancyId - the resident's tenancy.
 * @param {string} messageId - the warning message.
 * @returns {Promise<ResidentMessage | null>}
 */
const acknowledgeWarning = async (
  tenancyId: string,
  messageId: string,
): Promise<ResidentMessage | null> => {
  if (isBackendConfigured() === false || tenancyId === '' || messageId === '') {
    return null
  }

  try {
    const response = await apiCall({
      group: RESIDENT_GROUP,
      command: 'acknowledgewarning',
      data: { tenancyId, messageId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ResidentMessage
  } catch (error) {
    console.warn('[resident] acknowledgeWarning failed —', error)
    return null
  }
}

export {
  claimTenancy,
  getMyTenancies,
  getMyLedger,
  getMyRentShare,
  getMyDeposit,
  getMyMessages,
  sendMyMessage,
  markMyThreadRead,
  getMyMessagesUnreadCount,
  acknowledgeWarning,
  getMyLeaseOffers,
  respondToLeaseOffer,
  createRentCheckout,
  createRentPaymentIntent,
  type RentPaymentIntentResult,
  getChargeBreakdown,
  createChargePaymentIntent,
  type ChargePaymentIntentResult,
  createAutopaySetup,
  createAutopaySetupIntent,
  setAutopay,
  getPaymentOptions,
  getMaintenance,
  getMaintenanceCategories,
  submitMaintenance,
  uploadMaintenanceImage,
  getMyAnnouncements,
  getMyDocuments,
  acknowledgeAnnouncement,
  getMyPolls,
  votePoll,
  getBuildingAmenities,
  getAmenityAvailability,
  bookAmenity,
  startAmenityDeposit,
  getMyAmenityBookings,
  cancelMyAmenityBooking,
  getCommunityPosts,
  createCommunityPost,
  uploadCommunityPostImage,
  getCommunityPostReplies,
  replyToCommunityPost,
  reportCommunityPost,
  setCommunityPostResolution,
  removeCommunityPost,
  getFeedAvailability,
  getFeedTermsAccepted,
  acceptFeedTerms,
  voteFeedPost,
}
